import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { createBooking, updateBooking, checkBookingConflicts } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const OwnerUseDialog = ({
  open,
  onClose,
  selectedDate,
  properties,
  selectedProperty,
  editingBooking = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    check_in: '',
    check_out: '',
    notes: '',
  });
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (editingBooking) {
      setFormData({
        property_id: editingBooking.property_id || '',
        check_in: editingBooking.check_in || '',
        check_out: editingBooking.check_out || '',
        notes: editingBooking.notes || '',
      });
      if (editingBooking.check_in) setCheckInDate(new Date(editingBooking.check_in + 'T00:00:00'));
      if (editingBooking.check_out) setCheckOutDate(new Date(editingBooking.check_out + 'T00:00:00'));
    } else {
      setFormData({
        property_id: selectedProperty?.id || '',
        check_in: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
        check_out: '',
        notes: '',
      });
      if (selectedDate) setCheckInDate(selectedDate);
      setCheckOutDate(null);
    }
  }, [open, selectedProperty, selectedDate, editingBooking]);

  useEffect(() => {
    const run = async () => {
      if (formData.property_id && formData.check_in && formData.check_out) {
        try {
          const response = await checkBookingConflicts({
            property_id: formData.property_id,
            guest_id: 'owner-use',
            check_in: formData.check_in,
            check_out: formData.check_out,
          });
          // Filter out self if editing
          const filtered = (response.data.conflicts || []).filter(
            c => !editingBooking || c.booking_id !== editingBooking.id
          );
          setConflicts(filtered);
        } catch {
          setConflicts([]);
        }
      } else {
        setConflicts([]);
      }
    };
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [formData.property_id, formData.check_in, formData.check_out, editingBooking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id || !formData.check_in || !formData.check_out) {
      toast.error('Please select property and dates');
      return;
    }
    setLoading(true);
    try {
      if (editingBooking) {
        await updateBooking(editingBooking.id, {
          check_in: formData.check_in,
          check_out: formData.check_out,
          notes: formData.notes,
        });
        toast.success('Owner use block updated');
      } else {
        await createBooking({
          property_id: formData.property_id,
          guest_id: 'owner-use',
          guest_name: 'Owner Use',
          check_in: formData.check_in,
          check_out: formData.check_out,
          notes: formData.notes,
          total_price: 0,
          source_platform: 'owner_use',
        });
        toast.success('Owner use block created');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save owner use block');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[500px]" data-testid="owner-use-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope">
            {editingBooking ? 'Edit ' : ''}Owner Use Block
          </DialogTitle>
          <DialogDescription className="text-slate-500">Block dates for owner use — will not appear as a bookable window.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
              disabled={!!editingBooking}
            >
              <SelectTrigger data-testid="owner-use-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Start Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal bg-slate-50',
                    !checkInDate && 'text-muted-foreground'
                  )}
                  data-testid="owner-use-start-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkInDate ? format(checkInDate, 'PPP') : 'Select start'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkInDate}
                  onSelect={(date) => {
                    setCheckInDate(date);
                    setFormData(prev => ({ ...prev, check_in: date ? format(date, 'yyyy-MM-dd') : '' }));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>End Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal bg-slate-50',
                    !checkOutDate && 'text-muted-foreground'
                  )}
                  data-testid="owner-use-end-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkOutDate ? format(checkOutDate, 'PPP') : 'Select end'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkOutDate}
                  onSelect={(date) => {
                    setCheckOutDate(date);
                    setFormData(prev => ({ ...prev, check_out: date ? format(date, 'yyyy-MM-dd') : '' }));
                  }}
                  disabled={(date) => checkInDate && date <= checkInDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {conflicts.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-2 text-red-700 font-medium">
                <AlertTriangle className="h-4 w-4" />
                <span>Date Conflict</span>
              </div>
              <ul className="text-sm text-red-600">
                {conflicts.map((c) => (
                  <li key={c.booking_id || `${c.check_in}-${c.check_out}`}>
                    {c.guest_name} ({c.check_in} → {c.check_out})
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Family vacation, off-market, etc."
              rows={2}
              className="bg-slate-50"
              data-testid="owner-use-notes-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || conflicts.length > 0}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="owner-use-submit-btn"
            >
              {loading ? 'Saving...' : editingBooking ? 'Save Changes' : 'Block Dates'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OwnerUseDialog;
